import React from 'react'
import 'views/views.css'
import {
    LineChart as RechartLineChart,
    // CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Line,
    ResponsiveContainer,
} from 'recharts'
import { LoadingIndicator } from 'components'
import { clientTheme } from 'theme-exports'

// Format percentage values based on their magnitude
const formatPercentTick = (val: number): string => {
    if (val === 0) {
        return '0%'
    }
    if (val < 1) {
        return `${val.toFixed(2)}%`
    }
    return `${val.toFixed(1)}%`
}

const CustomTooltipValue = ({
    active,
    payload,
    label,
    toolTipValueName,
}: {
    toolTipValueName: string
    [key: string]: any
}) => {
    if (active && payload?.length) {
        return (
            <div
                className={'emp-lineChart-tooltipContainer'}
                style={{ fontSize: clientTheme.typography.body1.fontSize }}
            >
                <table style={{ borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr style={{ backgroundColor: '#b3b3b3' }}>
                            <td
                                colSpan={2}
                                className={'emp-lineChart-tooltipTableHeader '}
                            >
                                {label}
                            </td>
                        </tr>
                        <tr>
                            <td
                                className={'emp-lineChart-tooltipTableRow'}
                                style={{
                                    borderRight: '1px dotted #b3b3b3',
                                    textAlign: 'center',
                                }}
                            >
                                {toolTipValueName}
                            </td>
                            <td
                                className={'emp-lineChart-tooltipTableRow'}
                                style={{
                                    textAlign: 'right',
                                }}
                            >
                                {payload[0].value}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        )
    }

    return null
}

const CustomTooltipPercent = ({
    active,
    payload,
    label,
}: {
    [key: string]: any
}) => {
    if (active && payload?.length) {
        return (
            <div
                className={'emp-lineChart-tooltipContainer'}
                style={{ fontSize: clientTheme.typography.body1.fontSize }}
            >
                <table style={{ borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr style={{ backgroundColor: '#b3b3b3' }}>
                            <td className={'emp-lineChart-tooltipTableHeader'}>
                                {label}
                            </td>
                        </tr>

                        <tr>
                            <td
                                className={'emp-lineChart-tooltipTableRow'}
                                style={{
                                    textAlign: 'center',
                                }}
                            >
                                {formatPercentTick(payload[0].value)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        )
    }

    return null
}

/**
 * @name LineChart
 */
const LineChart = ({
    data = [],
    loading = false,
    yAxisPercent = false,
    toolTipValueName = 'Value',
    tickCount = 5,
    disableYAxisAutoDomain = false,
    customYAxisDomain = undefined,
}: {
    data: { name: string; val: string | number }[]
    loading?: boolean
    yAxisPercent?: boolean
    toolTipValueName?: string
    tickCount?: number
    disableYAxisAutoDomain?: boolean // Disable 0 - 100% domain on some percentage charts.
    customYAxisDomain?: [number, number] // Custom domain for Y-axis [min, max]
}): React.ReactElement => {
    return (
        <div className={'emp-lineChart-root'}>
            {!loading ? (
                <>
                    {data.length ? (
                        <ResponsiveContainer height={265} width="100%">
                            <RechartLineChart
                                data={data.map((dataPoint) => ({
                                    ...dataPoint,
                                    val: +dataPoint.val,
                                }))}
                                margin={{
                                    top: 20,
                                    left: -10,
                                    bottom: 5,
                                    right: 5,
                                }}
                            >
                                {/* <CartesianGrid strokeDasharray="3 3" /> */}
                                <XAxis
                                    dataKey="name"
                                    tick={{
                                        fontSize:
                                            clientTheme.typography.label
                                                .fontSize,
                                    }}
                                />
                                {yAxisPercent ? (
                                    <YAxis
                                        {...(disableYAxisAutoDomain
                                            ? { interval: 0 }
                                            : {})}
                                        tickCount={tickCount}
                                        domain={
                                            customYAxisDomain ||
                                            (disableYAxisAutoDomain
                                                ? undefined
                                                : yAxisPercent
                                                ? [
                                                      0,
                                                      (dataMax) =>
                                                          Math.min(
                                                              Math.round(
                                                                  dataMax * 1.1
                                                              ),
                                                              100
                                                          ),
                                                  ]
                                                : undefined)
                                        }
                                        tickFormatter={formatPercentTick}
                                        tick={{
                                            fontSize:
                                                clientTheme.typography.label
                                                    .fontSize,
                                        }}
                                    />
                                ) : (
                                    <YAxis
                                        tick={{
                                            fontSize:
                                                clientTheme.typography.label
                                                    .fontSize,
                                        }}
                                    />
                                )}
                                {yAxisPercent ? (
                                    <Tooltip
                                        content={<CustomTooltipPercent />}
                                    />
                                ) : (
                                    <Tooltip
                                        content={
                                            <CustomTooltipValue
                                                toolTipValueName={
                                                    toolTipValueName
                                                }
                                            />
                                        }
                                    />
                                )}
                                <Line
                                    type="monotone"
                                    dataKey="val"
                                    stroke={
                                        clientTheme.charts.lineCharts.stroke
                                    }
                                    fill={clientTheme.charts.lineCharts.fill}
                                />
                            </RechartLineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className={'emp-lineChart-noDataMessaging'}>
                            No Data
                        </div>
                    )}
                </>
            ) : (
                <div className={'emp-lineChart-loadingContainer'}>
                    <LoadingIndicator />
                </div>
            )}
        </div>
    )
}

export default LineChart
